import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import {
  FulfillmentGroup,
  FulfillmentGroupData,
  IFulfillmentGroupData,
} from "@redotech/redo-model/fulfillments/fulfillment-group";
import { PickSession } from "@redotech/redo-model/wms/schema/pick-session";
import { Input } from "@redotech/ui/form";
import { ImmutableCountMap } from "@redotech/util/map";
import { Dispatch, ReactNode, SetStateAction } from "react";
import { ScanMode } from "../common";

export enum ScanningModal {
  FULFILLMENT_GROUP_SELECTION = "FULFILLMENT_GROUP_SELECTION",
  TOTE_ORDER_SELECTION = "TOTE_ORDER_SELECTION",
  CLOSED_GROUP_WARNING = "CLOSED_GROUP_WARNING",
  INCOMPLETE_VERIFICATION = "INCOMPLETE_VERIFICATION",
  PARTIAL_FULFILL = "PARTIAL_FULFILL",
  WRONG_BARCODE = "WRONG_BARCODE",
  MERGED_PACKING_SLIP_SCANNED = "MERGED_PACKING_SLIP_SCANNED",
}

type ModalActionPayload =
  | { type: ScanningModal.FULFILLMENT_GROUP_SELECTION }
  | { type: ScanningModal.TOTE_ORDER_SELECTION }
  | { type: ScanningModal.CLOSED_GROUP_WARNING }
  | {
      type: ScanningModal.INCOMPLETE_VERIFICATION;
      // Path to navigate to
      data: string;
    }
  | {
      type: ScanningModal.WRONG_BARCODE;
      // Barcode that was scanned
      data: string;
    }
  | { type: ScanningModal.PARTIAL_FULFILL }
  | {
      type: ScanningModal.MERGED_PACKING_SLIP_SCANNED;
      data: FulfillmentGroupData;
    };

export enum ScanningActionType {
  OPEN_MODAL = "OPEN_MODAL",
  CLOSE_MODAL = "CLOSE_MODAL",
  SET_FULFILLMENT_GROUPS = "SET_FULFILLMENT_GROUPS",
  SET_FULFILLMENT_GROUP = "SET_FULFILLMENT_GROUP",
  SET_FULFILLMENT_GROUP_DATA = "SET_FULFILLMENT_GROUP_DATA",
  SELECT_FULFILLMENT_GROUP = "SELECT_FULFILLMENT_GROUP",
  SET_PICK_SESSION = "SET_PICK_SESSION",
  SET_ORDER_SELECTION = "SET_ORDER_SELECTION",
  SET_SCANNED = "SET_SCANNED",
  SET_FULFILLMENT_GROUP_LOADING = "SET_FULFILLMENT_GROUP_LOADING",
}

export type ScanningContextType = {
  scanMode: ScanMode;
  setScanMode: (mode: ScanMode) => void;

  handleActionButtonClick: () => void;
  // Scan handling
  handleScan: (barcode: Input<string>) => void;
  handlePartialFulfill: () => void;
  partialFulfillPending?: boolean;

  // Loading states
  pending: boolean;

  // Actions
  scanned: Scanned;
  setScanned: (scanned: Scanned) => void;

  fulfillmentGroupData?: FulfillmentGroupData | null;
  setFulfillmentGroupData: (data: FulfillmentGroupData | null) => void;
  reloadFulfillmentGroupData: () => void;
  fulfillmentGroupDataPending: boolean;

  // Fulfillment group operations
  updateFulfillmentGroups: (updates: FulfillmentGroupUpdate[]) => void;
  updatesPending: boolean;

  // UI elements
  progress?: ReactNode;

  setFulfillmentActionButton: Dispatch<
    SetStateAction<HTMLButtonElement | null>
  >;

  modalState?: ScanningModal;
};

export type Scanned = ImmutableCountMap<string>;

export type ScanningState = {
  _fulfillmentGroupIds: string[];
  _fulfillmentGroupId?: string;

  modalState?: ScanningModal;
  modalData?: any;

  fulfillmentGroupIds: string[];
  fulfillmentGroups: Record<string, IFulfillmentGroupData>;
  fulfillmentGroupData?: FulfillmentGroupData | null;

  relevantFulfillmentGroups: FulfillmentGroup[];
  pickSession?: PickSession;
  scanned: Scanned;
};

export type ScanningAction =
  | { type: ScanningActionType.OPEN_MODAL; payload: ModalActionPayload }
  | { type: ScanningActionType.CLOSE_MODAL }
  | { type: ScanningActionType.SET_PICK_SESSION; payload: PickSession }
  | {
      type: ScanningActionType.SET_FULFILLMENT_GROUPS;
      payload: IFulfillmentGroupData[];
    }
  | { type: ScanningActionType.SET_FULFILLMENT_GROUP; payload: string }
  | {
      type: ScanningActionType.SET_FULFILLMENT_GROUP_DATA;
      payload: FulfillmentGroupData | null;
    }
  | {
      type: ScanningActionType.SELECT_FULFILLMENT_GROUP;
      payload: FulfillmentGroup[];
    }
  | { type: ScanningActionType.SET_SCANNED; payload: Scanned }
  | {
      type: ScanningActionType.SET_FULFILLMENT_GROUP_LOADING;
      payload: boolean;
    };
